import axios from 'axios'

/**
 * Cliente axios da loja pública — separado do admin.
 * Base URL: /api/loja (nginx roteia pro backend)
 *
 * Auth: se houver token_cliente_loja salvo no localStorage, envia no header.
 * Se falhar com 401, limpa e redireciona pra login da loja.
 */
export const lojaApi = axios.create({
  baseURL: '/api/loja',
  headers: { 'Content-Type': 'application/json' },
})

const TOKEN_KEY = 'pain_loja_token'

export function setLojaToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export function getLojaToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

lojaApi.interceptors.request.use((config) => {
  const token = getLojaToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ============================================================
// TIPOS
// ============================================================
export interface LojaInfo {
  slug: string
  nome: string
  descricao: string | null
  cor_primaria: string
  whatsapp: string | null
  logo_url: string | null
  tipo_operacao: string
  permite_retirada: boolean
  permite_entrega: boolean
  taxa_entrega_fixa: number | null
  chave_pix: string | null
  chave_pix_nome: string | null
  endereco: {
    logradouro: string | null
    numero: string | null
    bairro: string | null
    cidade: string | null
    uf: string | null
    cep: string | null
  } | null
}

export interface LojaProduto {
  id: number
  sku: string
  ean: string | null
  descricao: string
  categoria_id: number | null
  unidade: string
  preco: number
  estoque: number
  disponivel: boolean
  foto: string | null
}

export interface LojaProdutoDetalhe extends LojaProduto {
  categoria_nome: string | null
  peso_liquido: number | null
  fotos: Array<{
    url_thumb: string
    url_medium: string
    url_full: string
    principal: boolean
  }>
}

export interface LojaCatalogoResponse {
  itens: LojaProduto[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface LojaSubgrupo {
  id: number
  nome: string
  qtd_produtos: number
}

export interface LojaCategoria {
  id: number
  nome: string
  qtd_produtos?: number
  subgrupos?: LojaSubgrupo[]
}

// ============================================================
// FUNÇÕES DE API
// ============================================================
export async function getInfo(slug: string): Promise<LojaInfo> {
  return (await lojaApi.get(`/${slug}/info`)).data
}

export async function getCatalogo(slug: string, params: {
  q?: string
  categoria_id?: number
  grupo_id?: number
  subgrupo_id?: number
  preco_min?: number
  preco_max?: number
  ordenar?: string
  page?: number
  page_size?: number
} = {}): Promise<LojaCatalogoResponse> {
  return (await lojaApi.get(`/${slug}/catalogo`, { params })).data
}

export async function getProduto(slug: string, sku: string): Promise<LojaProdutoDetalhe> {
  return (await lojaApi.get(`/${slug}/produto/${sku}`)).data
}

export async function getCategorias(slug: string): Promise<LojaCategoria[]> {
  return (await lojaApi.get(`/${slug}/categorias`)).data
}

// Formatador de moeda BR
export function fmtPreco(valor: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor)
}


// ============================================================
// AUTH CLIENTE-LOJA (LOJA-2B + LOJA-3)
// ============================================================

export interface ClienteLoja {
  id: number
  nome: string
  email: string
  telefone: string | null
  cpf_cnpj: string | null
  endereco: {
    cep: string | null
    logradouro: string | null
    numero: string | null
    complemento: string | null
    bairro: string | null
    cidade: string | null
    uf: string | null
  }
  email_verificado: boolean
  criado_em: string | null
}

export interface LoginResponse {
  token: string
  cliente: ClienteLoja
}

export async function cadastrar(slug: string, data: {
  nome: string
  email: string
  senha: string
  telefone?: string
  cpf_cnpj?: string
}): Promise<LoginResponse> {
  const r = (await lojaApi.post(`/${slug}/cadastrar`, data)).data
  setLojaToken(r.token)
  return r
}

export async function login(slug: string, email: string, senha: string): Promise<LoginResponse> {
  const r = (await lojaApi.post(`/${slug}/login`, { email, senha })).data
  setLojaToken(r.token)
  return r
}

export async function logout() {
  setLojaToken(null)
}

export async function getMe(): Promise<ClienteLoja> {
  return (await lojaApi.get('/me')).data
}

export async function atualizarMe(data: Partial<{
  nome: string
  telefone: string
  cpf_cnpj: string
  cep: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  uf: string
}>): Promise<ClienteLoja> {
  return (await lojaApi.put('/me', data)).data
}

// ============================================================
// PEDIDOS
// ============================================================

export interface CriarPedidoPayload {
  itens: Array<{ sku: string; quantidade: number }>
  tipo_entrega: 'retirada' | 'entrega'
  forma_pagamento: 'dinheiro' | 'pix' | 'cartao_debito' | 'cartao_credito' | 'boleto' | 'prazo'
  parcelas?: number
  observacao?: string
  endereco_cep?: string
  endereco_logradouro?: string
  endereco_numero?: string
  endereco_complemento?: string
  endereco_bairro?: string
  endereco_cidade?: string
  endereco_uf?: string
}

export interface PedidoCriado {
  id: number
  numero: number
  status: string
  total: number
  rastreio_token: string
  url_rastreio: string
}

export async function criarPedido(slug: string, payload: CriarPedidoPayload): Promise<PedidoCriado> {
  return (await lojaApi.post(`/${slug}/pedido`, payload)).data
}

export interface MeuPedido {
  id: number
  numero: number
  status: string
  total: number
  forma_pagamento: string
  parcelas: number
  criado_em: string | null
  rastreio_token: string | null
}

export async function getMeusPedidos(): Promise<{ itens: MeuPedido[]; total: number }> {
  return (await lojaApi.get('/me/pedidos')).data
}

// ============================================================
// RASTREIO PUBLICO
// ============================================================

export interface Rastreio {
  loja: { nome: string; whatsapp: string | null }
  pedido: {
    numero: number
    status: string
    total: number
    forma_pagamento: string
    parcelas: number
    criado_em: string | null
    transportadora: string | null
    rastreio_codigo: string | null
  }
  itens: Array<{ descricao: string; quantidade: number; preco_unitario: number; total: number }>
  timeline: Array<{ evento: string; em: string }>
}

export async function getRastreio(token: string): Promise<Rastreio> {
  return (await lojaApi.get(`/rastreio/${token}`)).data
}
